import React from "react"
import isJwt from "validator/lib/isJWT"
import { redirect } from "react-router-dom"

import { PrivateContainer } from "containers"
import { ErrorBoundary, ProtectedRoute } from "components"

const Dashboard = React.lazy(() => import("../pages/dashboard"))
const PaymentGateways = React.lazy(() => import("../pages/payment-gateways"))
const OrdersNotConfirmed = React.lazy(() => import("../pages/orders-not-confirmed"))
const FoodOptionList = React.lazy(() => import("../pages/food-option-list"))
const FoodFeatures = React.lazy(() => import("../pages/food-features"))
const EditFoodFeature = React.lazy(() => import("../pages/edit-food-feature"))
const AddFoodFeatures = React.lazy(() => import("../pages/add-food-feature"))
const Branches = React.lazy(() => import("../pages/branches"))
const AddFood = React.lazy(() => import("../pages/add-food"))
const OrdersPaid = React.lazy(() => import("../pages/orders-paid"))
const OrdersPending = React.lazy(() => import("../pages/orders-pending"))
const OrdersDelivered = React.lazy(() => import("../pages/orders-delivered"))
const OrdersCanceled = React.lazy(() => import("../pages/orders-canceled"))
const OrdersProblematic = React.lazy(() => import("../pages/orders-problematic"))
const MarketplaceOrders = React.lazy(() => import("../pages/marketplace-orders"))
const Locations = React.lazy(() => import("../pages/locations"))
const Settings = React.lazy(() => import("../pages/settings"))
const FoodList = React.lazy(() => import("../pages/food-list"))
const EditFood = React.lazy(() => import("../pages/edit-food"))
const Customers = React.lazy(() => import("../pages/customers"))
const CustomerDashboard = React.lazy(() => import("../pages/customer-dashboard"))
const Coupones = React.lazy(() => import("../pages/coupons"))
const Reports = React.lazy(() => import("../pages/reports"))
const WalletTransactions = React.lazy(() => import("../pages/wallet-transactions"))
const Categories = React.lazy(() => import("../pages/categories"))
const TaxesCharges = React.lazy(() => import("../pages/taxes-and-charges"))
const AddCombo = React.lazy(() => import("../pages/add-combo"))
const ComboList = React.lazy(() => import("../pages/combo-list"))
const EditComboSet = React.lazy(() => import("../pages/edit-combo"))
const AccountManagement = React.lazy(() => import("../pages/account-management"))
const ShiftHistory = React.lazy(() => import("../pages/shift-history"))
const PageDecoration = React.lazy(() => import("../pages/page-decoration"))
const AddSpecification = React.lazy(() => import("../pages/add-specification"))
const EditSpecification = React.lazy(() => import("../pages/edit-specification"))
const SpecificationList = React.lazy(() => import("../pages/specification-list"))
const AddAddOn = React.lazy(() => import("../pages/add-add-on"))
const EditAddOn = React.lazy(() => import("../pages/edit-add-on"))
const AddOnList = React.lazy(() => import("../pages/add-on-list"))
const Inventory = React.lazy(() => import("../pages/inventory"))
const StockReport = React.lazy(() => import("../pages/stock-report"))
const AddBuyXFreeN = React.lazy(() => import("../pages/add-buy-x-free-n"))
const EditBuyXFreeN = React.lazy(() => import("../pages/edit-buy-x-free-n"))
const RoleManagement = React.lazy(() => import("../pages/role-management"))
const RoleDetails = React.lazy(() => import("../pages/role-details"))
const AddBranchToFood = React.lazy(() => import("../pages/add-branch-to-food"))
const Media = React.lazy(() => import("../pages/media"))
const RepositoryManagement = React.lazy(() => import("../pages/repository-management"))
const RepositoryReport = React.lazy(() => import("../pages/repository-report"))
const ThemeManagement = React.lazy(() => import("../pages/theme-management"))
const CustomerPrintTemplate = React.lazy(() => import("../pages/customer-print-template"))
const Blogs = React.lazy(() => import("../pages/blogs"))
const AddBlog = React.lazy(() => import("../pages/add-blog"))
const EditBlog = React.lazy(() => import("../pages/edit-blog"))
const BlogComments = React.lazy(() => import("../pages/blog-comments"))
const SmsHistory = React.lazy(() => import("../pages/sms-history"))
const FoodDescriptionsList = React.lazy(() => import("../pages/food-descriptions-list"))
const SmsTemplates = React.lazy(() => import("../pages/sms-templates"))
const SmsPanel = React.lazy(() => import("../pages/sms-panel"))
const RecommendedFoods = React.lazy(() => import("../pages/recommended-foods"))
const FoodPriceList = React.lazy(() => import("../pages/food-price-list"))
const Campaigns = React.lazy(() => import("../pages/campaigns"))
const SubscriptionLevels = React.lazy(() => import("../pages/subscription-levels"))
const Achievements = React.lazy(() => import("../pages/achievements"))
const CouponUsageHistory = React.lazy(() => import("../pages/coupon-usage-history"))
const FarasodWalletTransactions = React.lazy(() => import("../pages/farasod-wallet-transactions"))
const MarketplaceSettings = React.lazy(() => import("../pages/marketplace-settings"))

const loader = () => {
  if (!isJwt(localStorage.at || "")) return redirect("/")

  return true
}

const privateRoutes = {
  loader,
  errorElement: <ErrorBoundary />,
  element: <PrivateContainer />,
  children: [
    {
      path: "/dashboard",
      element:
        <ProtectedRoute
          requiredAccesses={["viewDashboard"]}
        >
          <Dashboard />
        </ProtectedRoute>
    },
    {
      path: "/orders-paid",
      element:
        <ProtectedRoute
          requiredAccesses={["viewLatestOrders"]}
        >
          <OrdersPaid />
        </ProtectedRoute>
    },
    {
      path: "/orders-pending",
      element:
        <ProtectedRoute
          requiredAccesses={["viewLatestOrders"]}
        >
          <OrdersPending />
        </ProtectedRoute>
    },
    {
      path: "/coupon-usage-history/:couponId",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCouponsList"]}
        >
          <CouponUsageHistory />
        </ProtectedRoute>
    },
    {
      path: "/orders-delivered",
      element:
        <ProtectedRoute
          requiredAccesses={["viewOrderHistory"]}
        >
          <OrdersDelivered />
        </ProtectedRoute>
    },
    {
      path: "/orders-problematic",
      element:
        <ProtectedRoute
          requiredAccesses={["viewOrderHistory"]}
        >
          <OrdersProblematic />
        </ProtectedRoute>
    },
    {
      path: "/orders-canceled",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCanceledOrders"]}
        >
          <OrdersCanceled />
        </ProtectedRoute>
    },
    {
      path: "/orders-not-confirmed",
      element:
        <ProtectedRoute
          requiredAccesses={["viewLatestOrders"]}
        >
          <OrdersNotConfirmed />
        </ProtectedRoute>
    },
    {
      path: "/marketplace-orders",
      element:
        <ProtectedRoute
          requiredAccesses={["viewLatestOrders"]}
        >
          <MarketplaceOrders />
        </ProtectedRoute>
    },
    {
      path: "/payment-gateways",
      element:
        <ProtectedRoute
          requiredAccesses={["viewPaymentSecrets"]}
        >
          <PaymentGateways />
        </ProtectedRoute>
    },
    {
      path: "/stock-report/:foodId",
      element:
        <ProtectedRoute
          requiredAccesses={["inventory"]}
        >
          <StockReport />
        </ProtectedRoute>
    },
    {
      path: "/add-buy-x-free-n",
      element:
        <ProtectedRoute
          requiredAccesses={["coupon"]}
        >
          <AddBuyXFreeN />
        </ProtectedRoute>
    },
    {
      path: "/edit-buy-x-free-n",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCombosList"]}
        >
          <EditBuyXFreeN />
        </ProtectedRoute>
    },
    {
      path: "/taxes-and-charges",
      element:
        <ProtectedRoute
          requiredAccesses={["viewTaxList"]}
        >
          <TaxesCharges />
        </ProtectedRoute>
    },
    {
      path: "/inventory",
      element:
        <ProtectedRoute
          requiredAccesses={["inventory"]}
        >
          <Inventory />
        </ProtectedRoute>
    },
    {
      path: "/foods-branch",
      element:
        <ProtectedRoute
          requiredAccesses={["addFoodsToBranch"]}
        >
          <AddBranchToFood />
        </ProtectedRoute>
    },
    {
      path: "/page-decoration",
      element:
        <ProtectedRoute
          requiredAccesses={["viewPageDecoration"]}
        >
          <PageDecoration />
        </ProtectedRoute>
    },
    {
      path: "/add-specification",
      element:
        <ProtectedRoute
          requiredAccesses={["viewSpecificationList"]}
        >
          <AddSpecification />
        </ProtectedRoute>
    },
    {
      path: "/edit-specification/:specificationId",
      element:
        <ProtectedRoute
          requiredAccesses={["viewSpecificationList"]}
        >
          <EditSpecification />
        </ProtectedRoute>

    },
    {
      path: "/specification-list",
      element:
        <ProtectedRoute
          requiredAccesses={["viewSpecificationList"]}
        >
          <SpecificationList />
        </ProtectedRoute>
    },
    {
      path: "/add-add-on",
      element:
        <ProtectedRoute
          requiredAccesses={["inventory"]}
        >
          <AddAddOn />
        </ProtectedRoute>
    },
    {
      path: "/edit-add-on/:specificationId",
      element:
        <ProtectedRoute
          requiredAccesses={["viewAddOnList"]}
        >
          <EditAddOn />
        </ProtectedRoute>
    },
    {
      path: "/add-on-list",
      element:
        <ProtectedRoute
          requiredAccesses={["viewAddOnList"]}
        >
          <AddOnList />
        </ProtectedRoute>
    },
    {
      path: "/add-food",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodList"]}
        >
          <AddFood />
        </ProtectedRoute>
    },
    {
      path: "/categories",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCategories"]}
        >
          <Categories />
        </ProtectedRoute>
    },
    {
      path: "/reports",
      element:
        <ProtectedRoute
          requiredAccesses={["viewReports"]}
        >
          <Reports />
        </ProtectedRoute>
    },
    {
      path: "/settings",
      element:
        <ProtectedRoute
          requiredAccesses={["viewSystemSettings"]}
        >
          <Settings />
        </ProtectedRoute>
    },
    {
      path: "/add-combo",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCombosList"]}
        >
          <AddCombo />
        </ProtectedRoute>
    },
    {
      path: "/edit-combo",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCombosList"]}
        >
          <EditComboSet />
        </ProtectedRoute>
    },
    {
      path: "/combo-list",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCombosList"]}
        >
          <ComboList />
        </ProtectedRoute>
    },
    {
      path: "/customers",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCustomersList"]}
        >
          <Customers />
        </ProtectedRoute>
    },
    {
      path: "/customers/:accountId",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCustomersList"]}
        >
          <CustomerDashboard />
        </ProtectedRoute>
    },
    {
      path: "/coupons",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCouponsList"]}
        >
          <Coupones />
        </ProtectedRoute>
    },
    {
      path: "/locations",
      element: <Locations />
    },
    {
      path: "/food-list",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodList"]}
        >
          <FoodList />
        </ProtectedRoute>
    },
    {
      path: "/edit-food/:foodId",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodList"]}
        >
          <EditFood />
        </ProtectedRoute>
    },
    {
      path: "/wallet-transactions",
      element:
        <ProtectedRoute
          requiredAccesses={["viewWalletTransactions"]}
        >
          <WalletTransactions />
        </ProtectedRoute>
    },
    {
      path: "/farasod-wallet-transactions",
      element:
        <ProtectedRoute
          requiredAccesses={["viewWalletTransactions"]}
        >
          <FarasodWalletTransactions />
        </ProtectedRoute>
    },
    {
      path: "/account-management",
      element:
        <ProtectedRoute
          requiredAccesses={["viewStaff"]}
        >
          <AccountManagement />
        </ProtectedRoute>
    },
    {
      path: "/shift-history",
      element:
        <ProtectedRoute
          requiredAccesses={["viewShifts"]}
        >
          <ShiftHistory />
        </ProtectedRoute>
    },
    {
      path: "/role-management",
      element:
        <ProtectedRoute
          requiredAccesses={["viewAccessList"]}
        >
          <RoleManagement />
        </ProtectedRoute>

    },
    {
      path: "/role-details/:accessId",
      element:
        <ProtectedRoute
          requiredAccesses={["viewAccessList"]}
        >
          <RoleDetails />
        </ProtectedRoute>
    },
    {
      path: "/branches",
      element:
        <ProtectedRoute
          requiredAccesses={["viewBranches"]}
        >
          <Branches />
        </ProtectedRoute>
    },
    {
      path: "/marketplace-settings",
      element:
        <ProtectedRoute
          requiredAccesses={["viewSystemSettings"]}
        >
          <MarketplaceSettings />
        </ProtectedRoute>
    },
    {
      path: "/media",
      element:
        <ProtectedRoute
          requiredAccesses={["viewMedia"]}
        >
          <Media />
        </ProtectedRoute>
    },
    {
      path: "/repository-management",
      element:
        <ProtectedRoute
          requiredAccesses={["repositoryManagement"]}
        >
          <RepositoryManagement />
        </ProtectedRoute>
    },
    {
      path: "/repository-report",
      element:
        <ProtectedRoute
          requiredAccesses={["repositoryManagement"]}
        >
          <RepositoryReport />
        </ProtectedRoute>
    },
    {
      path: "/theme-management",
      element:
        <ProtectedRoute
          requiredAccesses={["viewThemeManagment"]}
        >
          <ThemeManagement />
        </ProtectedRoute>
    },
    {
      path: "/theme-management",
      element:
        <ProtectedRoute
          requiredAccesses={["viewThemeManagment"]}
        >
          <ThemeManagement />
        </ProtectedRoute>
    },
    {
      path: "/food-features",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodFeatures"]}
        >
          <FoodFeatures />
        </ProtectedRoute>
    },
    {
      path: "/add-food-feature",
      element:
        <ProtectedRoute
          requiredAccesses={["addFoodFeatures"]}
        >
          <AddFoodFeatures />
        </ProtectedRoute>
    },
    {
      path: "/edit-food-feature/:foodFeatureId",
      element:
        <ProtectedRoute
          requiredAccesses={["editFoodFeatures"]}
        >
          <EditFoodFeature />
        </ProtectedRoute>
    },
    {
      path: "/food-option-list",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodOptionList"]}
        >
          <FoodOptionList />
        </ProtectedRoute>
    },
    {
      path: "/customer-print-template",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCustomerPrintTemplate"]}
        >
          <CustomerPrintTemplate />
        </ProtectedRoute>
    },
    {
      path: "/blogs",
      element:
        <ProtectedRoute
          requiredAccesses={["viewBlogs"]}
        >
          <Blogs />
        </ProtectedRoute>
    },
    {
      path: "/add-blog",
      element:
        <ProtectedRoute
          requiredAccesses={["addBlog"]}
        >
          <AddBlog />
        </ProtectedRoute>
    },
    {
      path: "/edit-blog/:postId",
      element:
        <ProtectedRoute
          requiredAccesses={["editBlog"]}
        >
          <EditBlog />
        </ProtectedRoute>
    },
    {
      path: "/blog-comments/:postId",
      element:
        <ProtectedRoute
          requiredAccesses={["viewBlogComments"]}
        >
          <BlogComments />
        </ProtectedRoute>
    },
    {
      path: "/sms-panel",
      element:
        <ProtectedRoute
          requiredAccesses={["viewSmsPanel"]}
        >
          <SmsPanel />
        </ProtectedRoute>
    },
    {
      path: "/sms-templates",
      element:
        <ProtectedRoute
          requiredAccesses={["viewSmsTemplates"]}
        >
          <SmsTemplates />
        </ProtectedRoute>
    },
    {
      path: "/recommended-foods",
      element:
        <ProtectedRoute
          requiredAccesses={["viewRecommendedFoods"]}
        >
          <RecommendedFoods />
        </ProtectedRoute>
    },
    {
      path: "/food-price-list",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodPriceList"]}
        >
          <FoodPriceList />
        </ProtectedRoute>
    },
    {
      path: "/sms-history",
      element:
        <ProtectedRoute
          requiredAccesses={["viewSmsHistory"]}
        >
          <SmsHistory />
        </ProtectedRoute>
    },
    {
      path: "/campaigns",
      element:
        <ProtectedRoute
          requiredAccesses={["viewCampaigns"]}
        >
          <Campaigns />
        </ProtectedRoute>
    },
    {
      path: "/food-descriptions-list",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodLongDescriptionsList"]}
        >
          <FoodDescriptionsList />
        </ProtectedRoute>
    },
    {
      path: "/subscription-levels",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodLongDescriptionsList"]}
        >
          <SubscriptionLevels />
        </ProtectedRoute>
    },
    {
      path: "/achievements",
      element:
        <ProtectedRoute
          requiredAccesses={["viewFoodLongDescriptionsList"]}
        >
          <Achievements />
        </ProtectedRoute>
    }
  ]
}

export default privateRoutes
